import type { MockContext } from '@mocks/mockFetch';
import { MOCK_GRAFANA_TARGETS_BY_SITE } from '@mocks/data/grafanaTargets';

type MockHandler = (ctx: MockContext) => Promise<Response | null> | Response | null;

const respond = (payload: unknown, init?: ResponseInit) =>
  new Response(JSON.stringify(payload), {
    status: 200,
    headers: { 'Content-Type': 'application/json', 'X-Mock-Route': 'dashboards', ...(init?.headers ?? {}) },
    ...init
  });

const matchOrgDashboardsPath = (url: URL) => {
  // Be liberal in what we accept: in dev the UI may be configured with /public/v1, /v1, or a prefixed path.
  // We only care that the path contains /orgs/:orgId/<dashboards|grafana>/...
  const parts = url.pathname.split('/').filter(Boolean);
  const orgsIndex = parts.indexOf('orgs');
  if (orgsIndex < 0 || orgsIndex + 1 >= parts.length) {
    return null;
  }

  const orgId = decodeURIComponent(parts[orgsIndex + 1] ?? '');
  const tailParts = parts.slice(orgsIndex + 2);
  const tail = tailParts.join('/');
  if (!tail) {
    return null;
  }

  if (!tail.startsWith('dashboards/') && tail !== 'dashboards' && !tail.startsWith('grafana/')) {
    return null;
  }

  return { orgId, tail };
};

const matchAdminConnectorRuntimePath = (url: URL) => {
  const parts = url.pathname.split('/').filter(Boolean);
  if (parts.length < 5 || parts[0] !== 'admin' || parts[1] !== 'v1') {
    return null;
  }
  const orgsIndex = parts.indexOf('orgs');
  if (orgsIndex < 0 || orgsIndex + 1 >= parts.length) {
    return null;
  }
  const orgId = decodeURIComponent(parts[orgsIndex + 1] ?? '');
  const tail = parts.slice(orgsIndex + 2).join('/');
  if (tail !== 'connector-runtime-metrics') {
    return null;
  }
  return { orgId };
};

const handleConnectorRuntimeMetrics: MockHandler = ({ request, url }) => {
  if (request.method !== 'GET') {
    return null;
  }
  const match = matchAdminConnectorRuntimePath(url);
  if (!match) {
    return null;
  }

  return respond({
    orgId: match.orgId,
    status: 'degraded',
    summary: 'Connector runtime telemetry is reporting, with mild export pressure and one stale VPN path.',
    scope: {
      level: 'org',
      orgId: match.orgId
    },
    connectors: [
      { id: 'connector-a', name: 'connector-a', series: 138 },
      { id: 'connector-b', name: 'connector-b', series: 124 },
      { id: 'connector-c', name: 'connector-c', series: 96 }
    ],
    window: url.searchParams.get('window') ?? '1h',
    generatedAt: new Date().toISOString(),
    refreshSeconds: 30,
    links: {
      dashboard: '/mock-grafana-iframe.html?panelId=900',
      embed: '/mock-grafana-iframe.html?panelId=901'
    },
    diagnostics: {
      metricFamilies: [
        { name: 'otel_agent_sync_age_seconds', series: 3 },
        { name: 'otel_agent_sync_failures_total', series: 3 },
        { name: 'command_agent_export_failures_total', series: 3 },
        { name: 'connector_vpn_tunnel_up', series: 6 },
        { name: 'otelcol_exporter_sent_metric_points', series: 9 },
        { name: 'otelcol_exporter_send_failed_metric_points', series: 6 },
        { name: 'otelcol_exporter_queue_size', series: 6 },
        { name: 'otelcol_exporter_queue_capacity', series: 6 },
        { name: 'otelcol_receiver_accepted_metric_points', series: 9 },
        { name: 'otelcol_processor_batch_batch_send_size', series: 3 },
        { name: 'otelcol_process_uptime', series: 3 },
        { name: 'process_resident_memory_bytes', series: 3 },
        { name: 'go_goroutines', series: 3 }
      ]
    },
    sections: [
      {
        key: 'otelAgent',
        label: 'OTEL Agent',
        status: 'healthy',
        summary: 'Agents are syncing and scraping successfully across active connectors.',
        reporting: {
          state: 'reporting',
          label: 'Reporting from 3 connectors',
          missingFamilies: []
        },
        kpis: [
          { id: 'sync-age', label: 'Sync age', value: 42, unit: 'seconds', window: 'Current', severity: 'good', description: 'Latest fleet sync age.' },
          { id: 'sync-fail', label: 'Sync failures', value: 0.02, unit: 'rate', window: '5m rate', severity: 'good', precision: 2, description: 'Recent sync failures per second.' },
          { id: 'scrape-drop', label: 'Dropped samples', value: 0.01, unit: 'rate', window: '5m rate', severity: 'neutral', precision: 2, description: 'Scrape drops after retries.' }
        ],
        charts: [],
        breakdowns: [{ label: 'Drops by connector', rows: [{ label: 'connector-a', value: 0 }, { label: 'connector-b', value: 1 }, { label: 'connector-c', value: 0 }] }],
        lastUpdated: new Date().toISOString()
      },
      {
        key: 'commandAgent',
        label: 'Command Agent',
        status: 'degraded',
        summary: 'One connector is showing mild export queue pressure.',
        reporting: {
          state: 'reporting',
          label: 'Reporting from 3 connectors',
          missingFamilies: ['command_agent_queue_latency_seconds']
        },
        kpis: [
          { id: 'export-fail', label: 'Export failures', value: 0.08, unit: 'rate', window: '5m rate', severity: 'bad', precision: 2, description: 'Prometheus export failures.' },
          { id: 'queue-depth', label: 'Queue depth', value: 73, unit: 'count', window: 'Current', severity: 'bad', description: 'Buffered exports pending send.' },
          { id: 'restarts', label: 'Restarts', value: 1, unit: 'count', window: 'Last 1h', severity: 'neutral', description: 'Process restarts in the last hour.' }
        ],
        charts: [],
        breakdowns: [{ label: 'Queue depth by connector', rows: [{ label: 'connector-a', value: 12 }, { label: 'connector-b', value: 73 }, { label: 'connector-c', value: 8 }] }],
        lastUpdated: new Date().toISOString()
      },
      {
        key: 'connectorVpn',
        label: 'Connector VPN',
        status: 'degraded',
        summary: 'Most VPN paths are healthy, with one stale path still recovering.',
        reporting: {
          state: 'reporting',
          label: 'Reporting from 3 connectors',
          missingFamilies: []
        },
        kpis: [
          { id: 'vpn-health', label: 'VPN healthy', value: 83, unit: 'percent', window: 'Current', severity: 'bad', precision: 0, description: 'Healthy connector VPN paths.' },
          { id: 'stale-paths', label: 'Stale paths', value: 1, unit: 'count', window: 'Current', severity: 'bad', description: 'Paths with stale health updates.' }
        ],
        charts: [],
        breakdowns: [{ label: 'VPN status by connector', rows: [{ label: 'connector-a', value: 1 }, { label: 'connector-b', value: 0 }, { label: 'connector-c', value: 0 }] }],
        lastUpdated: new Date().toISOString()
      }
    ]
  });
};

const handleTemplates: MockHandler = ({ request, url }) => {
  if (request.method !== 'GET') {
    return null;
  }
  const match = matchOrgDashboardsPath(url);
  if (!match || match.tail !== 'dashboards/templates') {
    return null;
  }

  return respond({
    pageKeys: [
      {
        key: 'org.cockpit',
        title: 'Org Cockpit',
        description: 'Org-wide health, active incidents, recommended widgets.'
      },
      {
        key: 'site.cockpit',
        title: 'Site Cockpit',
        description: 'Site-wide health, telemetry hot spots, and recommended widgets.'
      }
    ],
    widgetTypes: [
      {
        type: 'grafana_panel_iframe',
        bindings: { vars: ['site_id', 'target_id', 'device_id', 'metric_name', 'instance', 'if_name', 'condition_key'], time: ['from', 'to', 'asOf'] }
      }
    ],
    grafanaCatalog: [
      {
        templateKey: 'site.telemetry.interface_util_topk',
        dashUid: 'reap-site-telemetry',
        panelId: 11,
        mode: 'solo',
        vars: ['site_id'],
        timeVars: ['from', 'to']
      },
      {
        templateKey: 'site.telemetry.lag_members_up_ratio',
        dashUid: 'reap-site-telemetry',
        panelId: 12,
        mode: 'solo',
        vars: ['site_id'],
        timeVars: ['from', 'to']
      },
      {
        templateKey: 'site.telemetry.interface_util_detail',
        dashUid: 'reap-site-telemetry',
        panelId: 13,
        mode: 'solo',
        vars: ['site_id', 'target_id'],
        timeVars: ['from', 'to']
      },
      {
        templateKey: 'site.telemetry.lag_members_up_ratio_target',
        dashUid: 'reap-site-telemetry',
        panelId: 14,
        mode: 'solo',
        vars: ['site_id', 'target_id'],
        timeVars: ['from', 'to']
      },
      {
        templateKey: 'site.telemetry.lag_member_utilization_target',
        dashUid: 'reap-site-telemetry',
        panelId: 15,
        mode: 'solo',
        vars: ['site_id', 'target_id'],
        timeVars: ['from', 'to']
      },
      {
        templateKey: 'site.telemetry.interface_state_detail',
        dashUid: 'reap-site-telemetry',
        panelId: 16,
        mode: 'solo',
        vars: ['site_id', 'target_id'],
        timeVars: ['from', 'to']
      },
      {
        templateKey: 'site.telemetry.interface_error_rate_detail',
        dashUid: 'reap-site-telemetry',
        panelId: 17,
        mode: 'solo',
        vars: ['site_id', 'target_id'],
        timeVars: ['from', 'to']
      },
      {
        templateKey: 'site.telemetry.bgp_peer_established_detail',
        dashUid: 'reap-site-telemetry',
        panelId: 18,
        mode: 'solo',
        vars: ['site_id', 'target_id'],
        timeVars: ['from', 'to']
      },
      {
        templateKey: 'site.telemetry.bgp_reset_churn_detail',
        dashUid: 'reap-site-telemetry',
        panelId: 19,
        mode: 'solo',
        vars: ['site_id', 'target_id'],
        timeVars: ['from', 'to']
      },
      {
        templateKey: 'site.telemetry.device_cpu_p95_detail',
        dashUid: 'reap-site-telemetry',
        panelId: 20,
        mode: 'solo',
        vars: ['site_id', 'target_id'],
        timeVars: ['from', 'to']
      },
      {
        templateKey: 'site.telemetry.device_mem_p95_detail',
        dashUid: 'reap-site-telemetry',
        panelId: 21,
        mode: 'solo',
        vars: ['site_id', 'target_id'],
        timeVars: ['from', 'to']
      },
      {
        templateKey: 'site.telemetry.device_uptime_detail',
        dashUid: 'reap-site-telemetry',
        panelId: 37,
        mode: 'solo',
        vars: ['site_id', 'target_id'],
        timeVars: ['from', 'to']
      },
      {
        templateKey: 'site.telemetry.tunnel_state_detail',
        dashUid: 'reap-site-telemetry',
        panelId: 22,
        mode: 'solo',
        vars: ['site_id', 'device_id'],
        timeVars: ['from', 'to']
      },
      {
        templateKey: 'site.telemetry.bfd_session_state_detail',
        dashUid: 'reap-site-telemetry',
        panelId: 23,
        mode: 'solo',
        vars: ['site_id', 'device_id'],
        timeVars: ['from', 'to']
      },
      {
        templateKey: 'site.telemetry.bfd_transition_churn_detail',
        dashUid: 'reap-site-telemetry',
        panelId: 24,
        mode: 'solo',
        vars: ['site_id', 'device_id'],
        timeVars: ['from', 'to']
      },
      {
        templateKey: 'site.telemetry.environment_max_temp_detail',
        dashUid: 'reap-site-telemetry',
        panelId: 25,
        mode: 'solo',
        vars: ['site_id', 'target_id'],
        timeVars: ['from', 'to']
      },
      {
        templateKey: 'site.telemetry.environment_failed_fan_count_detail',
        dashUid: 'reap-site-telemetry',
        panelId: 26,
        mode: 'solo',
        vars: ['site_id', 'target_id'],
        timeVars: ['from', 'to']
      },
      {
        templateKey: 'site.telemetry.environment_psu_redundancy_ratio_detail',
        dashUid: 'reap-site-telemetry',
        panelId: 27,
        mode: 'solo',
        vars: ['site_id', 'target_id'],
        timeVars: ['from', 'to']
      },
      {
        templateKey: 'site.telemetry.signal_device_detail',
        dashUid: 'reap-site-telemetry',
        panelId: 28,
        mode: 'solo',
        vars: ['site_id', 'device_id', 'metric_name'],
        timeVars: ['from', 'to']
      },
      {
        templateKey: 'site.telemetry.signal_device_instance_detail',
        dashUid: 'reap-site-telemetry',
        panelId: 29,
        mode: 'solo',
        vars: ['site_id', 'device_id', 'metric_name', 'instance'],
        timeVars: ['from', 'to']
      },
      {
        templateKey: 'site.telemetry.signal_interface_detail',
        dashUid: 'reap-site-telemetry',
        panelId: 30,
        mode: 'solo',
        vars: ['site_id', 'device_id', 'metric_name', 'if_name'],
        timeVars: ['from', 'to']
      },
      {
        templateKey: 'site.telemetry.feature_detail',
        dashUid: 'reap-site-telemetry',
        panelId: 31,
        mode: 'solo',
        vars: ['site_id', 'target_id', 'metric_name'],
        timeVars: ['from', 'to']
      },
      {
        templateKey: 'site.telemetry.feature_instance_detail',
        dashUid: 'reap-site-telemetry',
        panelId: 32,
        mode: 'solo',
        vars: ['site_id', 'target_id', 'metric_name', 'instance'],
        timeVars: ['from', 'to']
      },
      {
        templateKey: 'site.telemetry.condition_detail',
        dashUid: 'reap-site-telemetry',
        panelId: 33,
        mode: 'solo',
        vars: ['site_id', 'target_id', 'condition_key'],
        timeVars: ['from', 'to']
      },
      {
        templateKey: 'site.telemetry.device_cpu_high_timeline',
        dashUid: 'reap-site-telemetry',
        panelId: 34,
        mode: 'solo',
        vars: ['site_id', 'target_id'],
        timeVars: ['from', 'to']
      },
      {
        templateKey: 'site.telemetry.device_memory_pressure_timeline',
        dashUid: 'reap-site-telemetry',
        panelId: 35,
        mode: 'solo',
        vars: ['site_id', 'target_id'],
        timeVars: ['from', 'to']
      },
      {
        templateKey: 'site.telemetry.device_telemetry_freshness_detail',
        dashUid: 'reap-site-telemetry',
        panelId: 36,
        mode: 'solo',
        vars: ['site_id', 'device_id'],
        timeVars: ['from', 'to']
      },
      {
        templateKey: 'org.telemetry.pipeline_sync_age',
        dashUid: 'reap-org-telemetry',
        panelId: 40,
        mode: 'solo',
        vars: ['site_id', 'device_id'],
        timeVars: ['from', 'to']
      },
      {
        templateKey: 'org.telemetry.pipeline_export_failures',
        dashUid: 'reap-org-telemetry',
        panelId: 41,
        mode: 'solo',
        vars: ['site_id', 'device_id'],
        timeVars: ['from', 'to']
      },
      {
        templateKey: 'org.telemetry.pipeline_dropped_samples',
        dashUid: 'reap-org-telemetry',
        panelId: 42,
        mode: 'solo',
        vars: ['site_id', 'device_id'],
        timeVars: ['from', 'to']
      },
      {
        templateKey: 'org.telemetry.pipeline_queue_pressure',
        dashUid: 'reap-org-telemetry',
        panelId: 43,
        mode: 'solo',
        vars: ['site_id', 'device_id'],
        timeVars: ['from', 'to']
      },
      {
        templateKey: 'org.telemetry.pipeline_process_restarts',
        dashUid: 'reap-org-telemetry',
        panelId: 44,
        mode: 'solo',
        vars: ['site_id', 'device_id'],
        timeVars: ['from', 'to']
      },
      {
        templateKey: 'org.telemetry.pipeline_vpn_health',
        dashUid: 'reap-org-telemetry',
        panelId: 45,
        mode: 'solo',
        vars: ['site_id', 'device_id'],
        timeVars: ['from', 'to']
      },
      {
        templateKey: 'org.telemetry.top_impacted_sites',
        dashUid: 'reap-org-telemetry',
        panelId: 46,
        mode: 'solo',
        vars: [],
        timeVars: ['from', 'to']
      },
      {
        templateKey: 'org.telemetry.stale_devices',
        dashUid: 'reap-org-telemetry',
        panelId: 47,
        mode: 'solo',
        vars: [],
        timeVars: ['from', 'to']
      },
      {
        templateKey: 'org.telemetry.reporting_coverage',
        dashUid: 'reap-org-telemetry',
        panelId: 48,
        mode: 'solo',
        vars: [],
        timeVars: ['from', 'to']
      },
      {
        templateKey: 'org.telemetry.device_health_active_by_site',
        dashUid: 'reap-org-telemetry',
        panelId: 49,
        mode: 'solo',
        vars: [],
        timeVars: ['from', 'to']
      },
      {
        templateKey: 'site.telemetry.condition_instance_detail',
        dashUid: 'reap-site-telemetry',
        panelId: 50,
        mode: 'solo',
        vars: ['site_id', 'target_id', 'condition_key', 'instance'],
        timeVars: ['from', 'to']
      }
    ],
    version: 'mock'
  });
};

const handleRecommended: MockHandler = ({ request, url }) => {
  if (request.method !== 'GET') {
    return null;
  }
  const match = matchOrgDashboardsPath(url);
  if (!match || match.tail !== 'dashboards/recommended') {
    return null;
  }

  const scopeType = url.searchParams.get('scopeType') ?? 'org';
  const scopeId = url.searchParams.get('scopeId') ?? match.orgId;
  const pageKey = url.searchParams.get('pageKey') ?? 'org.cockpit';
  const from = url.searchParams.get('from') ?? 'now-1h';
  const to = url.searchParams.get('to') ?? 'now';

  // When running in mocks, point iframes at a lightweight local HTML page so UI refresh behavior can be
  // validated without a live Grafana/Prometheus stack.
  const iframeBase = `/mock-grafana-iframe.html`;
  const timeArgs = [`from=${encodeURIComponent(from)}`, `to=${encodeURIComponent(to)}`].join('&');

  if (pageKey === 'org.cockpit') {
    return respond({
      pageKey,
      context: { scopeType, scopeId },
      widgets: [
        {
          id: 'w-org-interface-util',
          type: 'grafana_panel_iframe',
          title: 'Top interface utilization across org',
          bindings: { vars: {} },
          iframeUrl: `${iframeBase}?panelId=111&${timeArgs}`
        },
        {
          id: 'w-org-device-pressure',
          type: 'grafana_panel_iframe',
          title: 'Devices under CPU or memory pressure',
          bindings: { vars: {} },
          iframeUrl: `${iframeBase}?panelId=112&${timeArgs}`
        }
      ]
    });
  }

  return respond({
    pageKey,
    context: { scopeType, scopeId },
    widgets: [
      {
        id: 'w-interface-util',
        type: 'grafana_panel_iframe',
        title: 'Interface util pct (top)',
        bindings: { vars: { site_id: scopeId } },
        iframeUrl: `${iframeBase}?panelId=11&var-site_id=${encodeURIComponent(scopeId)}&${timeArgs}`
      },
      {
        id: 'w-lag-members',
        type: 'grafana_panel_iframe',
        title: 'LAG members up ratio',
        bindings: { vars: { site_id: scopeId } },
        iframeUrl: `${iframeBase}?panelId=12&var-site_id=${encodeURIComponent(scopeId)}&${timeArgs}`
      }
    ]
  });
};

const handleTelemetryTargets: MockHandler = ({ request, url }) => {
  if (request.method !== 'GET') {
    return null;
  }
  const match = matchOrgDashboardsPath(url);
  if (!match || match.tail !== 'dashboards/telemetry-targets') {
    return null;
  }

  const scopeType = url.searchParams.get('scopeType') ?? '';
  const scopeId = url.searchParams.get('scopeId') ?? '';
  if (scopeType !== 'site' || !scopeId) {
    return respond(
      { type: 'invalid-request', title: 'Invalid request', status: 400, detail: 'scopeType=site and scopeId are required' },
      { status: 400 }
    );
  }

  const items = (MOCK_GRAFANA_TARGETS_BY_SITE[scopeId] ?? []).map((target) => ({
    targetId: target.targetId,
    targetLabel: target.targetLabel,
    targetKind: target.targetKind,
    deviceName: target.deviceName
  }));

  return respond(
    { items },
    {
      headers: {
        ETag: 'W/"mock-telemetry-targets"',
        'Cache-Control': 'private, max-age=15'
      }
    }
  );
};

const handleEmbedSession: MockHandler = ({ request, url }) => {
  if (request.method !== 'POST') {
    return null;
  }
  const match = matchOrgDashboardsPath(url);
  if (!match || match.tail !== 'grafana/embed-session') {
    return null;
  }

  // In the real system this sets an HttpOnly cookie. In mocks, just return 204.
  return new Response(null, { status: 204, headers: { 'X-Mock-Route': 'dashboards' } });
};

const handleRecommendedGrafana: MockHandler = ({ request, url }) => {
  if (request.method !== 'GET') {
    return null;
  }
  const match = matchOrgDashboardsPath(url);
  if (!match || match.tail !== 'dashboards/recommended-grafana') {
    return null;
  }

  const scopeId = url.searchParams.get('scopeId') ?? match.orgId;
  const iframeBase = `/mock-grafana-iframe.html`;
  return respond({
    items: [
      {
        title: 'Interface util pct (top)',
        iframeUrl: `${iframeBase}?panelId=11&var-site_id=${encodeURIComponent(scopeId)}&from=now-1h&to=now`,
        why: 'Mock recommendation'
      },
      {
        title: 'LAG members up ratio',
        iframeUrl: `${iframeBase}?panelId=12&var-site_id=${encodeURIComponent(scopeId)}&from=now-1h&to=now`,
        why: 'Mock recommendation'
      }
    ]
  });
};

export const dashboardsHandlers: MockHandler[] = [
  handleConnectorRuntimeMetrics,
  handleEmbedSession,
  handleTemplates,
  handleTelemetryTargets,
  handleRecommended,
  handleRecommendedGrafana
];
